import * as React from "react";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {FormProvider, useForm} from "react-hook-form";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  BrickMailFragment,
  BrickMailGetDocument,
  BrickMailMailsInput,
  BrickMailValidation,
  BrickMailValidationResultEnum,
  EnumBrickMailMailsMailType,
  useBrickMailGetQuery,
  useBrickMailTestMutation,
  useBrickMailUpdateMutation
} from "../../../Queries";
import {ParamBrandHouse} from "../../../Parameters";
import {SavingUI} from "../../../SavingProvider";
import SettingsHeader from "../SettingsHeader";
import {usePrompt} from "../../../components/useBlocker";
import {ObjectHistory} from "../../components/ObjectHistory";
import {TitleText} from "../../../layout/Typography";
import PageContent from "../../../layout/PageContent";
import {FormData, MailEditorEnum} from "./types/MailTypes";
import {ClipboardProvider} from "./Clipboard";
import {MailEditorMain} from "./components/MailEditorMain";

export const MailEditPagePath = (brandHouseId: string, shopId: string, mailType: string, language?: string) => {
  let path = "/brands/" + brandHouseId + "/settings/mails/" + shopId + "/" + mailType;
  if (language) {
    path += '/' + language
  }
  return path
}

export const MailEditPage = () => {
  const {brandHouseId, language} = useParams<ParamBrandHouse & { language: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [sent, setSent] = useState<string>('');
  const [receiver, setReceiver] = useState<string | undefined>(localStorage.getItem('c2s-testmail') || undefined);
  const [consumer, setConsumer] = useState<string | undefined>(localStorage.getItem('c2s-testconsumer') || undefined);
  const [activations, setActivations] = useState<boolean>(localStorage.getItem('c2s-activations') === 'true');

  const [saveMessages, setSaveMessages] = useState<BrickMailValidation[]>([])
  const {shopId, mailType} = useParams<{ shopId: string, mailType: string }>();
  const navigate = useNavigate();

  const {data} = useBrickMailGetQuery({
    variables: {
      shopId: shopId,
      type: mailType
    }
  });

  const [save] = useBrickMailUpdateMutation();
  const [testMail] = useBrickMailTestMutation();

  const form = useForm<FormData>();
  const {setValue, handleSubmit, watch, formState: {isDirty}} = form;

  useEffect(() => {
    if (data) {
      const brickMail = data.BrickMail;
      const mails: BrickMailFragment[] = []
      let mainEditorType: EnumBrickMailMailsMailType = EnumBrickMailMailsMailType.BRICK
      for (const mail of brickMail?.mails || []) {
        let mailJson: string | undefined = undefined;
        if (mail?.mail) {
          mailJson = JSON.stringify(mail.mail, null, 2)
        } else {
          mailJson = undefined;
        }
        let editorType = mail!.mailType || EnumBrickMailMailsMailType.BRICK;
        mails.push({
          mail: mailJson,
          htmlMail: mail!.htmlMail,
          language: mail!.language,
          mailType: editorType
        })
        if (mainEditorType !== editorType) {
          mainEditorType = editorType
        }
      }
      const formData: FormData = {
        mails: mails as any,
        shopId: shopId,
        type: brickMail!.type!,
        editor: mainEditorType
      };
      form.reset(formData);
    }
  }, [data, form, shopId])

  usePrompt('Unsaved changes', isDirty);

  if (data === undefined) {
    return <div>Loading...</div>
  }

  const allLanguages = data?.ShopById?.tradeInLocales || []
  const selectedLanguage = language || allLanguages[0] || undefined;
  const mailData = data?.BrickMail?.mails

  const onChange = (event: React.SyntheticEvent, newValue: string) => {
    navigate(MailEditPagePath(brandHouseId, shopId, mailType, newValue), {
      replace: true
    });
  }
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen((previousOpen) => !previousOpen);
  };

  const onTest = async () => {
    if (receiver) {
      localStorage.setItem('c2s-testmail', receiver);
      localStorage.setItem('c2s-activations', String(activations));
      if (consumer) {
        localStorage.setItem('c2s-testconsumer', consumer);
      } else {
        localStorage.removeItem('c2s-testconsumer')
      }
      const {data} = await testMail({
        variables: {
          shopId: shopId,
          type: mailType,
          email: receiver,
          language: selectedLanguage!,
          activations: activations,
          testMail: consumer
        }
      });
      setSent(data?.BrickMailTest || 'Sent, maybe...');
      setOpen(false);
    }
  }

  const onSubmit = async (data: FormData) => {
    await SavingUI.process(async () => {
      setSaveMessages([]);

      const saveResult = await save({
        variables: {
          shopId: shopId,
          type: mailType,
          input: data.mails.map(mail => ({
            mail: mail.mail ? JSON.parse(mail.mail) : undefined,
            htmlMail: mail.htmlMail,
            language: mail.language!,
            mailType: data.editor === MailEditorEnum.Json ? EnumBrickMailMailsMailType.BRICK : data.editor
          } as BrickMailMailsInput))
        },
        refetchQueries: [{
          query: BrickMailGetDocument,
          variables: {
            shopId: shopId,
            type: mailType
          }
        }]
      });
      const errors = saveResult.data?.BrickMailUpdate?.filter(upd => upd?.result !== BrickMailValidationResultEnum.SUCCESS) || [];
      setSaveMessages(errors as BrickMailValidation[])
      return false;
    }, 'Done')
  }

  const editors = [{
    name: "Brick",
    id: EnumBrickMailMailsMailType.BRICK
  }, {
    name: "Advanced",
    id: MailEditorEnum.Json
  }];

  return (
    <ClipboardProvider>
      <FormProvider {...form}>
        <SettingsHeader title={data?.BrickMailTypeByID?.title || mailType} onBack>
          <Button variant={"contained"} color={"secondary"} onClick={handleClick}>Send test mail</Button>
          <Dialog open={open} onClose={() => setOpen(false)}>
            <Paper style={{padding: '20px'}}>
              <div>
                <TitleText type={"h3"}>Send test mail to:</TitleText>
                <TextField
                  style={{width: '400px'}}
                  value={receiver}
                  color={"secondary"}
                  onInput={(e: any) => setReceiver(e.target.value)} focused variant={"outlined"}
                  placeholder={"Receivers email"}
                />
              </div>
              <div style={{"marginTop": "20px"}}>
                <TitleText type={"h3"}>Use data from consumer mail:</TitleText>
                <TextField
                  style={{width: '400px'}}
                  value={consumer}
                  color={"secondary"}
                  onInput={(e: any) => setConsumer(e.target.value)} focused variant={"outlined"}
                  placeholder={"Consumer email"}
                />
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '10px'}}>
                <FormControlLabel
                  control={<Checkbox checked={activations} onChange={() => setActivations(!activations)}
                                    name="activations"/>}
                  label="Outline activations"/>
                <Button onClick={onTest} style={{height: '100%'}} variant={"contained"}>Send</Button>
              </div>
            </Paper>
          </Dialog>
          <Snackbar
            anchorOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            autoHideDuration={2000} open={!!sent} message={sent} onClose={() => setSent('')}/>
          <Button variant={"contained"} onClick={handleSubmit(onSubmit)}>Save</Button>
        </SettingsHeader>
        
        {(saveMessages.filter(msg => msg.locale === language).length > 0) && (
          <PageContent>
            {saveMessages!.filter(err => err.locale === language).map(err => {
              return (
                <div key={'error_' + err.locale}>
                  <Alert 
                    onClose={() => setSaveMessages(saveMessages.filter(msg => msg.locale !== language))}
                    severity={"error"}
                  >
                    {JSON.stringify(err.message, null, 2)}
                  </Alert>
                </div>
              );
            })}
          </PageContent>
        )}
        
        <PageContent style={{position: 'relative'}}>
          <Tabs value={selectedLanguage} onChange={onChange}>
            {allLanguages.map(language => {
              const messages = saveMessages.filter(msg => msg.locale === language)
              return (
                <Tab 
                  key={'tab_' + language}
                  label={
                    <Badge 
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      color={"primary"}
                      badgeContent={messages.length || undefined}
                    >
                      {language}
                    </Badge>
                  }
                  value={language}
                />
              );
            })}
          </Tabs>
          
          {mailData?.map((mail, index) => {
            const mailType = watch(`editor`)
            
            if (selectedLanguage !== mail?.language) {
              return null;
            }
            
            const onEditorTypeChange = (event: React.SyntheticEvent, newValue: string) => {
              setValue(`editor`, newValue, {
                shouldDirty: false
              });
            }
            
            return (
              <React.Fragment key={`mail_${index}`}>
                <Box sx={{
                  marginBottom: '10px',
                  borderBottom: '1px solid #e0e0e0',
                  textAlign: 'right',
                  position: 'absolute',
                  right: '25px',
                  marginTop: '5px'
                }}>
                  <Box>
                    <ToggleButtonGroup value={mailType} onChange={onEditorTypeChange} exclusive={true} size={"small"}>
                      {editors.map(editor => (
                        <ToggleButton key={'mailType_' + editor.id} value={editor.id}>
                          {editor.name}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                <MailEditorMain 
                  index={index} 
                  selectedLanguage={selectedLanguage!}
                  mailType={mailType}
                />
              </React.Fragment>
            );
          })}
        </PageContent>
        <ObjectHistory refId={data.BrickMail?._id}/>
      </FormProvider>
    </ClipboardProvider>
  );
}